import React, { SyntheticEvent, useState } from 'react';

import { SvgIcon } from '@material-ui/core';

import { down } from '@/assets/icons';
import { AsaphDropdownMenu } from '@/components/shared/AsaphDropdownMenu';
import { RoundedButton } from '@/components/shared/RoundedButton';
import { ReportsDateRange } from '@/hooks/useReportsAccess';

import styles from './DateRangeButton.module.scss';

export type DateRangeButtonProps = {
	value: ReportsDateRange;
	onChange: (value: ReportsDateRange) => void;
	// New optional props for year selector (only active when value === 'year'):
	availableYears?: number[];
	selectedYear?: number;
	onYearChange?: (year: number) => void;
};

const LABELS: Record<ReportsDateRange, string> = {
	'last-30': 'Last 30 days',
	'last-90': 'Last 90 days',
	'year': 'Calendar year',
	'full': 'All time', // not surfaced in UI — hook default only
};

export const DateRangeButton: React.FC<DateRangeButtonProps> = ({
	value,
	onChange,
	availableYears,
	selectedYear,
	onYearChange,
}) => {
	const [anchorEl, setAnchorEl] = useState<Element | null>(null);
	const [yearAnchorEl, setYearAnchorEl] = useState<Element | null>(null);

	const handleOpen = (e: SyntheticEvent) => {
		setAnchorEl(e.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleSelect = (next: ReportsDateRange) => {
		onChange(next);
		handleClose();
	};

	const handleYearOpen = (e: React.SyntheticEvent) => setYearAnchorEl(e.currentTarget);
	const handleYearClose = () => setYearAnchorEl(null);
	const yearOpen = !!yearAnchorEl;

	const open = !!anchorEl;

	return (
		<div className={styles['date-range-button']}>
			<RoundedButton size="md" onClick={handleOpen}>
				{LABELS[value]}
				<SvgIcon component={down} className={styles['chevron']} />
			</RoundedButton>
			<AsaphDropdownMenu
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				selectable
				items={[
					{
						label: 'Last 30 days',
						selected: value === 'last-30',
						onClick: () => handleSelect('last-30'),
					},
					{
						label: 'Last 90 days',
						selected: value === 'last-90',
						onClick: () => handleSelect('last-90'),
					},
					{
						label: 'Calendar year',
						selected: value === 'year',
						onClick: () => handleSelect('year'),
					},
				]}
			/>
			{value === 'year' && availableYears && availableYears.length > 0 && (
				<div className={styles['year-selector']}>
					<RoundedButton size="md" onClick={handleYearOpen}>
						{selectedYear ?? new Date().getFullYear()}
						<SvgIcon component={down} className={styles['chevron']} />
					</RoundedButton>
					<AsaphDropdownMenu
						open={yearOpen}
						anchorEl={yearAnchorEl}
						onClose={handleYearClose}
						selectable
						items={availableYears.map((y) => ({
							label: String(y),
							selected: y === selectedYear,
							onClick: () => { onYearChange?.(y); handleYearClose(); },
						}))}
					/>
				</div>
			)}
		</div>
	);
};
